import {
  Component,
  computed,
  effect,
  inject,
  input,
  Input,
  OnDestroy,
  untracked,
} from '@angular/core';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { CategoryStore } from '../../../stores/category/category.state';
import {
  PickListModule,
  PickListMoveToSourceEvent,
  PickListMoveToTargetEvent,
} from 'primeng/picklist';
import { Indicator } from '../../../models/indicator';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { IndicatorFilterComponent } from '../indicator-filter/indicator-filter.component';
import { DividerModule } from 'primeng/divider';
import { buildSortedIndicatorList } from '../../../stores/indicator/indicator.selectors';
import { units } from '../../../models/unit';
import { TooltipModule } from 'primeng/tooltip';
import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
import { TagSummaryPipe } from '../../../pipes/tag-summary.pipe';
import { Access } from '../../../models/rbac';
import { PermissionService } from '../../../services/permission.service';
import {
  applySetMove,
  normalizeSetSelection,
  SetMoveContext,
  SetSelectionRule,
  WHOLE_SETS_RULE,
} from '../../../stores/indicator/indicator-set-selection';

/** How an indicator is rendered in the lists. */
export type IndicatorRowKind = 'set' | 'child' | 'single';

/**
 * Pick list for indicators backed by the `IndicatorStore` selection.
 *
 * The store selection is the single source of truth, both lists are derived from it
 * (and from the current filter). Moves are translated by the `setRule`
 * (see `applySetMove`): a picked set is always the set with all of its children,
 * single children can be picked if the rule allows it.
 */
@Component({
  selector: 'app-indicator-pick-list',
  standalone: true,
  imports: [
    PickListModule,
    NgClass,
    NgTemplateOutlet,
    IndicatorFilterComponent,
    DividerModule,
    TooltipModule,
    PickListHeaderComponent,
    TagSummaryPipe,
  ],
  templateUrl: './indicator-pick-list.component.html',
  styleUrl: './indicator-pick-list.component.scss',
})
export class IndicatorPickListComponent implements OnDestroy {
  readonly action = input<keyof Access>();
  /** What can be picked - defaults to whole sets only. */
  readonly setRule = input<SetSelectionRule>(WHOLE_SETS_RULE);
  @Input() targetHeaderSubtitle?: string;
  @Input() keepSelection = false;

  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  private readonly permissionService = inject(PermissionService);

  protected readonly units = units;

  private readonly selectedIds = computed(
    () => new Set(this.indicatorStore.selection().map(i => i._id))
  );

  /**
   * Indicators available for the current action, already filtered.
   * For 'export' only indicators with export rights are offered.
   */
  private readonly availableIndicators = computed(() =>
    this.action() === 'export'
      ? this.permissionService.indicatorsWithExportRights()
      : this.indicatorStore.filteredItems()
  );

  private readonly matchingIds = computed(
    () =>
      new Set(
        [
          ...this.availableIndicators(),
          ...this.indicatorStore.filteredSelection(),
        ].map(indicator => indicator._id)
      )
  );

  /** Filtered, tree-sorted indicators which are not selected yet. */
  readonly sourceList = computed(() => {
    const selectedIds = this.selectedIds();
    const list = buildSortedIndicatorList(
      this.categoryStore.asArray(),
      this.availableIndicators(),
      this.indicatorStore.allGroupedChildren(),
      this.indicatorStore.onlySets()
    ).filter(indicator => !selectedIds.has(indicator._id));
    return this.withoutEmptySets(list);
  });

  /**
   * Filtered, tree-sorted selection. Only sets which are actually selected
   * are added as grouping context, so the list never shows unselected items.
   */
  readonly targetList = computed(() =>
    buildSortedIndicatorList(
      this.categoryStore.asArray(),
      this.indicatorStore.filteredSelection(),
      this.indicatorStore.allGroupedChildren(),
      this.indicatorStore.selection().filter(indicator => indicator.isSet)
    )
  );

  /** Sets in either list which are only shown as grouping for matching children. */
  readonly contextIds = computed(() => {
    const matching = this.matchingIds();
    return new Set(
      [...this.sourceList(), ...this.targetList()]
        .filter(indicator => !matching.has(indicator._id))
        .map(indicator => indicator._id)
    );
  });

  /** Number of selected indicators currently hidden by the filter. */
  readonly hiddenSelectionCount = computed(() =>
    Math.max(0, this.selectedIds().size - this.targetList().length)
  );

  /** Summary for the target header, e.g. "9 Indikatoren · 1 Set". */
  readonly selectionLabel = computed(() => {
    const selection = this.indicatorStore.selection();
    const sets = selection.filter(indicator => indicator.isSet).length;
    const indicators = `${selection.length - sets} Indikatoren`;
    if (!sets) return indicators;
    return `${indicators} · ${sets} ${sets === 1 ? 'Set' : 'Sets'}`;
  });

  /** Explains how sets are handled for the current rule. */
  readonly ruleHint = computed(() => {
    const { sets, children } = this.setRule();
    if (sets && children)
      return 'Ein Set wird immer komplett übernommen. Unterindikatoren sind auch einzeln wählbar.';
    if (sets) return 'Unterindikatoren werden immer mit ihrem Set übernommen.';
    return 'Sets selbst sind nicht wählbar – ein Set übernimmt seine Unterindikatoren.';
  });

  constructor() {
    // A kept selection (or a changed rule) must follow the current rule.
    effect(
      () => {
        this.setRule();
        untracked(() => {
          if (!this.indicatorStore.selection().length) return;
          this.indicatorStore.updateSelection(
            normalizeSetSelection(this.moveContext())
          );
        });
      },
      { allowSignalWrites: true }
    );
  }

  /**
   * Adds the moved items to the selection, translated by the set rule.
   *
   * @param {PickListMoveToTargetEvent} event - Contains the moved indicators.
   * @param {boolean} moveAll - Whether the event comes from "move all".
   */
  addToSelection(event: PickListMoveToTargetEvent, moveAll = false): void {
    this.indicatorStore.updateSelection(
      applySetMove(this.moveContext(), event.items as Indicator[], 'add', {
        moveAll,
        matchingIds: this.matchingIds(),
      })
    );
  }

  /**
   * Removes the moved items from the selection, translated by the set rule.
   *
   * @param {PickListMoveToSourceEvent} event - Contains the moved indicators.
   */
  removeFromSelection(event: PickListMoveToSourceEvent): void {
    this.indicatorStore.updateSelection(
      applySetMove(this.moveContext(), event.items as Indicator[], 'remove')
    );
  }

  /**
   * Determines how an indicator is rendered: as set header, as child below its
   * set (both in the same list) or on its own.
   *
   * @param {Indicator} indicator - The indicator to render.
   * @return {IndicatorRowKind} The row kind.
   */
  rowKind(indicator: Indicator): IndicatorRowKind {
    if (indicator.isSet) return 'set';
    if (!indicator.parent) return 'single';
    const selected = this.selectedIds();
    return selected.has(indicator.parent) === selected.has(indicator._id)
      ? 'child'
      : 'single';
  }

  protected readonly missingCategoryHint =
    'Fehler: Diesem Indikator ist kein (gültiger) Ordner zugeordnet. Über die Sammelaktion „Verschieben“ lässt er sich einem Ordner zuordnen.';

  /**
   * Whether the indicator belongs to an existing category - indicators without
   * one are a data error and get marked in the lists.
   *
   * @param {Indicator} indicator - The indicator.
   * @return {boolean} True if the category exists.
   */
  hasCategory(indicator: Indicator): boolean {
    return (
      !!indicator.category && !!this.categoryStore.items()[indicator.category]
    );
  }

  /**
   * Whether the meta line shows the folder. Children below their set only show it
   * if it differs from the set's folder (or is missing).
   *
   * @param {Indicator} indicator - The indicator.
   * @return {boolean} True if the folder is shown.
   */
  showsFolder(indicator: Indicator): boolean {
    if (this.rowKind(indicator) !== 'child' || !this.hasCategory(indicator))
      return true;
    const set = this.indicatorStore.getParent(indicator._id);
    return !set || set.category !== indicator.category;
  }

  /**
   * Number of children of a set (independent of the filter).
   *
   * @param {Indicator} set - The set.
   * @return {number} The number of children.
   */
  childCount(set: Indicator): number {
    return this.indicatorStore.allGroupedChildren()[set._id]?.length ?? 0;
  }

  /**
   * Unit label (symbol or name) of an indicator.
   *
   * @param {Indicator} indicator - The indicator.
   * @return {string} The unit label, empty if unknown.
   */
  unitLabel(indicator: Indicator): string {
    const unit = units[indicator.unit];
    return unit ? unit.symbol || unit.name : '';
  }

  /**
   * Clears the selection on destroy unless `keepSelection` is set.
   */
  ngOnDestroy(): void {
    if (!this.keepSelection) this.indicatorStore.clearSelection();
  }

  private moveContext(): SetMoveContext {
    return {
      selection: this.indicatorStore.selection(),
      items: this.indicatorStore.items(),
      children: this.indicatorStore.allGroupedChildren(),
      rule: this.setRule(),
    };
  }

  /**
   * Without `rule.sets` a set is only a group header - drop it once none of
   * its children is left in the list.
   */
  private withoutEmptySets(list: Indicator[]): Indicator[] {
    if (this.setRule().sets) return list;
    const parents = new Set(list.map(indicator => indicator.parent));
    return list.filter(
      indicator => !indicator.isSet || parents.has(indicator._id)
    );
  }
}
